/**
 * Custom UI Utils
 */
const cuiUtils = {
    getComputed: {
        transitionDuration(element) {
            // transition-duration 값을 숫자로 변환 (ms)
            return parseFloat(window.getComputedStyle(element).transitionDuration) * 1000;
        },
        height(element) {
            return parseFloat(window.getComputedStyle(element).height);
        },
        zIndex(element) {
            return window.getComputedStyle(element).zIndex;
        },
    },

    getTransitionDuration(element) {
        return cuiUtils.getComputed.transitionDuration(element) ?? 0;
    },

    a11yKey(params = {}, event) {
        // 키보드 접근성
        const target = event.target;

        const defaultOptions = {
            type: 'default', // 'trap' or 'default'
            callback: null,
        };
        const options = { ...defaultOptions, ...params };

        const isVisible = (element) => {
            return element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0;
        };

        const root = event.currentTarget;
        const focusableElements = [
            ...root.querySelectorAll('a, button, input, select, details, textarea, [tabindex]:not([tabindex="-1"])'),
        ].filter(isVisible);

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const keyTab = event.key === 'Tab' || event.keyCode === 9;
        const keyShift = event.shiftKey;
        const isTabPressed = keyTab && !keyShift;
        const isShiftTabPressed = keyShift && keyTab;

        if (options.type === 'trap') {
            // kayboard trap
            if (target === lastElement && isTabPressed) {
                event.preventDefault();
                setTimeout(() => firstElement.focus(), 50);
            } else if (target === firstElement && isShiftTabPressed) {
                event.preventDefault();
                setTimeout(() => lastElement.focus(), 50);
            }
        } else {
            // default
            if (target === lastElement && isTabPressed) {
                if (typeof options.callback === 'function') options.callback();
            } else if (target === firstElement && isShiftTabPressed) {
                if (typeof options.callback === 'function') options.callback();
            }
        }
    },

    openSlide(slideElement, params = {}) {
        let isVisible = false;

        const defaultOptions = {
            activeElement: null,
            activeClass: 'is-active',
            duration: 300, // (ms)
            useAttrHidden: true, // hidden 속성 사용 여부
            on: {
                slideStart: null,
                slideEnd: null
            }
        }
        const options = { ...defaultOptions, ...params };

        slideElement.style.height = 0;
        slideElement.style.transition = `height ${options.duration}ms`;
        slideElement.style.overflow = 'hidden';
        if (options.useAttrHidden) {
            slideElement.hidden = false;
        } else {
            slideElement.style.display = 'block';
        }

        requestAnimationFrame(() => {
            isVisible = true;

            // slideElement.offsetHeight;
            slideElement.style.height = slideElement.scrollHeight + 'px';
            if (options.activeElement) options.activeElement.classList.add(options.activeClass);

            if (typeof options.on.slideStart === 'function') {
                options.on.slideStart();
            }

            setTimeout(() => {
                if (isVisible && (options.activeElement && options.activeElement.classList.contains(options.activeClass))) {
                    // css 초기화 - 반응형 대응
                    slideElement.style.height = '';
                    slideElement.style.transition = '';
                    slideElement.style.overflow = '';

                    if (typeof options.on.slideEnd === 'function') {
                        options.on.slideEnd();
                    }
                }
            }, options.duration);
        });
    },

    closeSlide(slideElement, params = {}) {
        let isVisible = true;

        const defaultOptions = {
            activeElement: null,
            activeClass: 'is-active',
            duration: 300, // (ms)
            useAttrHidden: true, // hidden 속성 사용 여부
            on: {
                slideStart: null,
                slideEnd: null
            }
        }
        const options = { ...defaultOptions, ...params };

        slideElement.style.height = slideElement.scrollHeight + 'px';
        slideElement.style.transition = `height ${options.duration}ms`;
        slideElement.style.overflow = 'hidden';
        if (options.activeElement) options.activeElement.classList.remove(options.activeClass);

        if (typeof options.on.slideStart === 'function') {
            options.on.slideStart();
        }

        requestAnimationFrame(() => {
            slideElement.style.height = 0;

            setTimeout(() => {
                if (isVisible && (options.activeElement && !options.activeElement.classList.contains(options.activeClass))) {
                    if (options.useAttrHidden) {
                        slideElement.hidden = true;
                    } else {
                        slideElement.style.display = 'none';
                    }

                    // css 초기화 - 반응형 대응
                    slideElement.style.height = '';
                    slideElement.style.transition = '';
                    slideElement.style.overflow = '';
                    slideElement.style.display = '';

                    if (typeof options.on.slideEnd === 'function') {
                        options.on.slideEnd();
                    }

                    isVisible = false;
                }
            }, options.duration);
        });
    }
}


/**
 * GNB - PC
 */
class CustomHeader extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            header: 'custom-header',
            navMenus: 'custom-header__nav',
            headerGroup: 'custom-header-group',
            announcementBar: 'announcement-bar',
            state: {
                active: 'is-active',
                current: 'is-current',
                hidden: 'is-scroll-hidden'
            },
        };

        this.isScrolling = false;
        this.lastScrollY = null;
        this.onScrollHandle = this.onScroll.bind(this);
        this.onCloseBackdropHandle = this.onCloseBackdrop.bind(this);
    }

    get backdrop() {
        return document.getElementById(`${this.classes.header}-backdrop`);
    }

    get headerGroup() {
        return this.closest(`.${this.classes.headerGroup}`);
    }

    get sideNav() {
        const getId = this.buttonMobileMenu.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    get topSearch() {
        const getId = this.buttonSearch.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    get announcementBar() {
        return this.headerGroup && this.headerGroup.querySelector(`.${this.classes.announcementBar}`);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
        this.onScroll();
        this.setCurrentMenu();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    initElements() {
        this.header = this;
        this.navMenus = this.header.querySelector(`.${this.classes.navMenus}`);
        this.buttonMobileMenu = this.header.querySelector('[data-mobile-menu="open"]');
        this.buttonSearch = this.header.querySelector('[data-search-menu="open"]');

        if (!this.backdrop) {
            const createBackdrop = document.createElement('div');
            createBackdrop.id = `${this.classes.header}-backdrop`;
            createBackdrop.classList.add('backdrop');
            document.body.prepend(createBackdrop);
        }
    }

    initEventListeners() {
        this.navMenus.addEventListener('mouseenter', this.onEnterMenu.bind(this), true);
        this.navMenus.addEventListener('focus', this.onEnterMenu.bind(this), true);
        this.navMenus.addEventListener('touchstart', this.onEnterMenu.bind(this));
        this.navMenus.addEventListener('mouseleave', this.onLeaveNavMenus.bind(this));
        this.navMenus.addEventListener('keydown', cuiUtils.a11yKey.bind(this, { callback: () => this.onLeaveNavMenus() }));
        window.addEventListener('scroll', this.onScrollHandle);
        window.addEventListener('resize', this.onScrollHandle);

        this.buttonMobileMenu?.addEventListener('click', this.onDrawSideNav.bind(this));
        this.buttonSearch?.addEventListener('click', this.onDrawSearch.bind(this));
    }

    removeEventListeners() {
        window.removeEventListener('scroll', this.onScrollHandle);
        window.removeEventListener('resize', this.onResizeHandle);
        this.removeEventBackdrop();
    }

    removeEventBackdrop() {
        this.backdrop?.removeEventListener('click', this.onCloseBackdropHandle);
    }

    initActiveState() {
        if (this.buttonSearch?.classList.contains(this.classes.state.active)) return;

        const prev = this.navMenus.querySelector(`.nav__depth1 >li.${this.classes.state.active}`);

        if (prev) {
            prev.classList.remove(this.classes.state.active);
            this.header.classList.remove(this.classes.state.active);

            const subDepth = prev.querySelector('.nav__subwrap');
            if (subDepth) {
                subDepth.hidden = true;
                this.backdrop?.classList.remove(this.classes.state.active);
            }
        }
    }

    setCurrentMenu() {
        const currentItem = this.navMenus.querySelector(`.nav__depth1 >li.${this.classes.state.current}`);
        if (currentItem) {
            currentItem.classList.add(this.classes.state.active);
        }
    }

    onEnterMenu(e) {
        const target = e.target;

        if (target.closest('.nav__depth1__link')) {
            if (this.topSearch?.hidden === false) {
                // 상단 검색바 열려있을 경우 닫기
                this.topSearch.onCloseSearch();
            }

            this.initActiveState();
            const item = target.closest('li');
            const subDepth = item.querySelector('.nav__subwrap');
            if (subDepth) {
                e.preventDefault(); // GNB 하위뎁스 노출되기 전에 링크 이동되는 것 방지 - 태블릿 touchstart 고려

                subDepth.hidden = false;
                subDepth.offsetHeight;
                this.backdrop?.classList.add(this.classes.state.active);
                this.backdrop?.addEventListener('click', this.onCloseBackdropHandle); // backdrop 이벤트 리스너 추가 - search backdrop과 중복 호출 방지

                // 2depth 메뉴 위치값 세팅
                if (!subDepth.classList.contains('nav__subwrap--mega')) {
                    const subDepthMenus = subDepth.querySelector('.nav__depth2');
                    const setPosition = () => {
                        let result;
                        const parentRect = this.navMenus.getBoundingClientRect();
                        const listRect = subDepthMenus.getBoundingClientRect();
                        const itemRect = item.getBoundingClientRect();
                        const left = parentRect.left;
                        const right = parentRect.right - listRect.width;
                        const center = (itemRect.width <= listRect.width) ? itemRect.left - (listRect.width - itemRect.width) / 2 : itemRect.left;

                        if (parentRect.right <= center + listRect.width) {
                            result = right;
                        } else if (parentRect.left >= center) {
                            result = left;
                        } else {
                            result = center;
                        }

                        return result;
                    }

                    subDepthMenus.style.transform = `translateX(${setPosition()}px)`;
                }
            } else {
                this.backdrop?.classList.remove(this.classes.state.active);
            }

            item.classList.add(this.classes.state.active);
            this.header.classList.add(this.classes.state.active);
        }
    }

    onLeaveNavMenus() {
        this.initActiveState();
        this.setCurrentMenu();
    }

    onCloseBackdrop() {
        this.onLeaveNavMenus();
        this.removeEventBackdrop(); // backdrop 이벤트 리스너 제거 - search backdrop과 중복 호출 방지
    }

    onDrawSideNav() {
        if (this.topSearch?.hidden === false) {
            // 상단 검색바 열려있을 경우 닫기
            this.topSearch.onCloseSearch();
        };

        // 모바일 메뉴
        this.sideNav.hidden = false;
        this.buttonMobileMenu.setAttribute('aria-expanded', true);
        this.header.classList.add(this.classes.state.active);
        document.documentElement.classList.add('custom-scroll-lock');

        const buttonClose = this.sideNav.querySelector('[data-mobile-menu="close"]');
        setTimeout(() => {
            this.sideNav.classList.add('is-open');
            this.sideNav.querySelector('.custom-sidenav__wrap').addEventListener('keydown', (event) => {
                cuiUtils.a11yKey({ type: 'trap' }, event); // 키보드 접근성
            });
            if (buttonClose) buttonClose.focus();
        }, 10);
    }

    onDrawSearch() {
        if (this.sideNav?.hidden === false) {
            // 모바일 메뉴 열려있을 경우 닫기
            this.sideNav.onCloseNav();
        };

        // 상단 검색바
        if (this.buttonSearch.classList.contains(this.classes.state.active)) {
            this.onCloseSearch();
            return;
        }

        this.backdrop?.classList.add(this.classes.state.active);
        this.header.classList.add(this.classes.state.active);

        const buttonClose = this.topSearch.querySelector('[data-search-menu="close"]');
        const duration = cuiUtils.getTransitionDuration(this.topSearch);
        cuiUtils.openSlide(this.topSearch, {
            activeElement: this.buttonSearch,
            duration: duration,
            on: {
                slideStart: () => {
                    this.topSearch.classList.add('is-open');
                    this.buttonSearch.setAttribute('aria-expanded', true);
                },
                slideEnd: () => {
                    this.topSearch.addEventListener('keydown', (event) => {
                        cuiUtils.a11yKey({
                            type: 'default',
                            callback: () => {
                                this.onCloseSearch();
                            }
                        }, event); // 키보드 접근성
                    });
                    if (buttonClose) buttonClose.focus();
                }
            }
        });
    }

    onCloseSearch() {
        this.topSearch.onCloseSearch();
    }

    onScroll() {
        if (this.isScrolling) return;

        this.isScrolling = true;
        requestAnimationFrame(() => {
            const scrY = window.scrollY;
            const startY = this.header.offsetHeight;
            const headerStartY = this.header.offsetTop;

            const setPositionSideNav = (h) => {
                // 모바일메뉴 위치 조정
                const headerHeight = cuiUtils.getComputed.height(this.header);
                const announcementBarHeight = h ?? 0;

                return this.sideNav.style.setProperty('--sidevnav-top', `${headerHeight + announcementBarHeight}px`);
            }

            // 이전 스크롤 값 초기화
            if (!this.lastScrollY) {
                this.lastScrollY = scrY;
            }

            if (scrY <= 0) {
                // 최상단일 경우 항상 노출
                this.header.classList.remove(this.classes.state.hidden);

                if (this.headerGroup && this.announcementBar) {
                    // 상단 알림바 노출되고 있을 경우
                    this.headerGroup.style.transform = `translateY(0)`;
                    setPositionSideNav(headerStartY);
                }
            } else {
                if (scrY >= startY) {
                    if (scrY < this.lastScrollY || this.header.classList.contains(this.classes.state.active)) {
                        // console.log('scroll-up');
                        this.header.classList.remove(this.classes.state.hidden);
                    } else {
                        // console.log('scroll-down');
                        this.header.classList.add(this.classes.state.hidden);
                    }
                }

                if (this.headerGroup && this.announcementBar) {
                    // 상단 알림바 노출되고 있을 경우
                    this.headerGroup.style.transform = `translateY(-${headerStartY + 1}px)`;
                    setPositionSideNav(-1);
                }
            }

            this.lastScrollY = scrY;
            this.isScrolling = false;
        });
    }
}
customElements.define('custom-header', CustomHeader);


/**
 * GNB - Mobile
 */
class CustomMobileSideNav extends HTMLElement {
    constructor() {
        super();

        this.attachShadow({ mode: 'open' });
        this.shadowRoot.innerHTML = `
            <slot></slot>
            <div part="backdrop"></div>
        `;

        this.classes = {
            sideNav: 'custom-sidenav',
            navMenus: 'custom-sidenav__nav',
            header: 'custom-header',
            state: {
                active: 'is-active',
                current: 'is-current'
            },
        };
    }

    get backdrop() {
        return this.shadowRoot.querySelector('[part="backdrop"]');
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
        this.onMediaQuery();
        this.setCurrentMenu();
    }

    initElements() {
        this.sideNav = this;
        this.buttonClose = this.sideNav.querySelector('[data-mobile-menu="close"]');
        this.navMenus = this.sideNav.querySelector(`.${this.classes.navMenus}`);
        this.header = document.querySelector(`.${this.classes.header}`);
    }

    initEventListeners() {
        this.navMenus.addEventListener('click', this.onEnterMenu.bind(this), true);
        this.backdrop?.addEventListener('click', this.onCloseNav.bind(this));
        this.buttonClose?.addEventListener('click', this.onCloseNav.bind(this));
    }

    initActiveState() {
        this.clearActiveState(1);
        this.clearActiveState(2);
    }

    clearActiveState(depth) {
        let prevItem;

        switch (depth) {
            case 1:
                prevItem = this.navMenus.querySelector(`.nav__depth1 >li.${this.classes.state.active}`);
                break;
            case 2:
                prevItem = this.navMenus.querySelector(`.nav__depth2 >li.${this.classes.state.active}`);
                break;
            default:
                break;
        }

        if (prevItem) {
            const subDepth = prevItem.querySelector('.nav__subwrap');
            if (subDepth) {
                this.closeMenu(prevItem, subDepth);
            }
        }
    }

    setCurrentMenu() {
        const currentItems = this.sideNav.querySelectorAll(`li.${this.classes.state.current}`);
        currentItems.forEach((item) => {
            const subDepth = item.querySelector('.nav__subwrap');
            if (subDepth) {
                item.classList.add(this.classes.state.active);
                this.openMenu(item, subDepth);
            }
        });
    }

    openMenu(item, subDepth) {
        const duration = cuiUtils.getTransitionDuration(subDepth);
        cuiUtils.openSlide(subDepth, {
            activeElement: item,
            duration: duration
        });
    }

    closeMenu(item, subDepth) {
        const duration = cuiUtils.getTransitionDuration(subDepth);
        cuiUtils.closeSlide(subDepth, {
            activeElement: item,
            duration: duration
        });
    }

    onEnterMenu(e) {
        const target = e.target;

        const collapse = (target, subWrapSelector, callback) => {
            const item = target.closest('li');
            const subDepth = item.querySelector(subWrapSelector);

            if (subDepth) {
                e.preventDefault();

                if (item.classList.contains(this.classes.state.active)) {
                    this.closeMenu(item, subDepth);
                } else {
                    if (typeof callback === 'function') callback();
                    this.openMenu(item, subDepth);
                }
            }
        };

        if (target.closest('.nav__depth1__link')) {
            // 1depth
            collapse(target, '.nav__subwrap', () => {
                this.clearActiveState(1);
                this.clearActiveState(2);
            });
        } else if (target.closest('.nav__depth2__link')) {
            // 2depth
            collapse(target, '.nav__subwrap', () => {
                this.clearActiveState(2);
            });
        }
    }

    onCloseNav() {
        if (this.sideNav.hidden === true) return;

        const headerbutton = this.header.querySelector('[data-mobile-menu="open"]');
        const sideNavInner = this.sideNav.querySelector(`.${this.classes.sideNav}__wrap`);
        const duration = cuiUtils.getTransitionDuration(sideNavInner);

        this.sideNav.classList.remove('is-open');
        this.header.classList.remove(this.classes.state.active);
        document.documentElement.classList.remove('custom-scroll-lock');
        setTimeout(() => {
            this.sideNav.hidden = true;
            headerbutton.setAttribute('aria-expanded', false);
            headerbutton.focus();
            this.initActiveState();
            this.setCurrentMenu();
        }, duration);

        // 언어팝업 열려있을 경우 닫기
        const langButton = this.sideNav.querySelector('[data-mobile-language="open"][aria-expanded="true"]');
        if (langButton) {
            const langPopupSelector = 'custom-language-popup';
            const langPopupEl = document.querySelector(langPopupSelector);

            if (langPopupEl) {
                customElements.whenDefined(langPopupSelector).then(() => {
                    if (typeof langPopupEl.onCloseLanguage === 'function') {
                        langPopupEl.onCloseLanguage(langButton);
                    }
                });
            }
        }
    }

    onMediaQuery() {
        const mm = {
            desktop: window.matchMedia('(min-width: 1280px)'),
        };

        const updateMatches = () => {
            if (mm.desktop.matches) {
                this.onCloseNav();
            }
        };

        updateMatches();
        mm.desktop.addEventListener('change', updateMatches);
    }
}
customElements.define('custom-mobile-sidenav', CustomMobileSideNav);


/**
 * Top Search
 */
class CustomTopSearch extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            topSearch: 'custom-top-search',
            inputSearch: 'custom-top-search__input--text',
            header: 'custom-header',
            state: {
                active: 'is-active',
            },
        };

        this.onCloseBackdropHandle = this.onCloseBackdrop.bind(this);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
    }

    disconnectedCallback() {
        this.removeEventBackdrop();
    }

    initElements() {
        this.topSearch = this;
        this.buttonClose = this.topSearch.querySelector('[data-search-menu="close"]');
        this.inputSearch = this.topSearch.querySelector(`.${this.classes.inputSearch}`);
        this.header = document.querySelector(`.${this.classes.header}`);
        this.backdrop = document.getElementById(`${this.classes.header}-backdrop`);

        this.onChangeValue();
    }

    initEventListeners() {
        this.buttonClose?.addEventListener('click', this.onCloseSearch.bind(this));
        this.inputSearch?.addEventListener('input', this.onChangeValue.bind(this));
        this.backdrop?.addEventListener('click', this.onCloseBackdropHandle);
    }

    removeEventBackdrop() {
        this.backdrop?.removeEventListener('click', this.onCloseBackdropHandle);
    }

    onChangeValue() {
        const button = this.topSearch.querySelector('.custom-top-search__button--clear');
        button.addEventListener('click', () => {
            button.style.display = '';
            this.inputSearch.value = '';
            this.inputSearch.focus();
        });

        if (this.inputSearch.value !== '') {
            button.style.display = 'block';
        } else {
            button.style.display = '';
        }
    }

    onCloseSearch() {
        if (this.topSearch.hidden === true) return;

        const headerbutton = this.header.querySelector('[data-search-menu="open"]');
        const duration = cuiUtils.getTransitionDuration(this.topSearch);
        cuiUtils.closeSlide(this.topSearch, {
            activeElement: headerbutton,
            duration: duration,
            on: {
                slideStart: () => {
                    this.topSearch.classList.remove('is-open');
                    this.header.classList.remove(this.classes.state.active);
                    this.backdrop?.classList.remove(this.classes.state.active);
                },
                slideEnd: () => {
                    headerbutton.setAttribute('aria-expanded', false);
                    headerbutton.focus();
                }
            }
        });
    }

    onCloseBackdrop() {
        this.onCloseSearch();
    }
}
customElements.define('custom-top-search', CustomTopSearch);


/**
 * Footer
 */
class CustomFooter extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            footer: 'custom-footer',
            goTopButton: 'custom-footer-gotop',
            sideNav: 'custom-sidenav',
            state: {
                active: 'is-active',
            },
        };
    }

    get footerBody() {
        const getId = this.toggleButton.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    get goTopButton() {
        return document.querySelector(`.${this.classes.goTopButton}`);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
        this.onScroll();
        this.onMediaQuery();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    initElements() {
        this.footer = this;
        this.toggleButton = this.footer.querySelector('[data-footer-menu="toggle"]');
    }

    initEventListeners() {
        this.toggleButton?.addEventListener('click', this.onToggleFnb.bind(this));
        this.goTopButton?.addEventListener('click', this.onPageTop.bind(this));
        window.addEventListener('scroll', this.onScroll.bind(this));
    }

    removeEventListeners() {
        window.removeEventListener('scroll', this.onScroll.bind(this));
    }

    openFnb() {
        cuiUtils.openSlide(this.footerBody, {
            activeElement: this.toggleButton,
            duration: 0,
            useAttrHidden: false,
            on: {
                slideStart: () => {
                    this.toggleButton.setAttribute('aria-expanded', true);
                },
                slideEnd: () => {
                    window.scrollTo({
                        top: document.documentElement.scrollHeight - window.innerHeight,
                        behavior: 'smooth',
                    });
                }
            }
        });
    }

    closeFnb() {
        cuiUtils.closeSlide(this.footerBody, {
            activeElement: this.toggleButton,
            duration: 0,
            useAttrHidden: false,
            on: {
                slideStart: () => {
                    this.toggleButton.setAttribute('aria-expanded', false);
                }
            }
        });
    }

    onToggleFnb() {
        if (this.footerBody) {
            if (this.toggleButton.classList.contains(this.classes.state.active)) {
                this.closeFnb();
            } else {
                this.openFnb();
            }
        }
    }

    initToggleState() {
        if (this.footerBody) {
            this.toggleButton.classList.remove(this.classes.state.active);
            this.toggleButton.setAttribute('aria-expanded', false);
            this.footerBody.style.display = '';
            this.footerBody.style.height = '';

            // 언어팝업 열려있을 경우 닫기
            const langButton = this.footerBody.querySelector('[data-mobile-language="open"][aria-expanded="true"]');
            if (langButton) {
                const langPopupSelector = 'custom-language-popup';
                const langPopupEl = document.querySelector(langPopupSelector);

                if (langPopupEl) {
                    customElements.whenDefined(langPopupSelector).then(() => {
                        if (typeof langPopupEl.onCloseLanguage === 'function') {
                            langPopupEl.onCloseLanguage(langButton);
                        }
                    });
                }
            }
        }
    }

    onPageTop(e) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onScroll() {
        if (!this.goTopButton) return;

        requestAnimationFrame(() => {
            const winH = window.innerHeight;
            const scrY = window.scrollY;
            const calcDocumentH = document.documentElement.scrollHeight - winH;
            const startY = (winH / 2);
            const offsetY = Math.ceil(scrY + winH);
            const footerStartY = this.footer.offsetTop;

            const triggerY = (startY > calcDocumentH) ? Math.abs(startY - (calcDocumentH + this.footer.offsetHeight)) : startY;

            if (scrY > triggerY) {
                this.goTopButton.classList.add(this.classes.state.active);
                if (offsetY >= footerStartY) {
                    this.goTopButton.classList.add('is-unfixed');
                } else {
                    this.goTopButton.classList.remove('is-unfixed');
                }
            } else {
                this.goTopButton.classList.remove(this.classes.state.active);
            }
        });
    }

    onMediaQuery() {
        const mm = {
            desktop: window.matchMedia('(min-width: 960px)')
        };

        const updateMatches = () => {
            if (mm.desktop.matches) {
                this.initToggleState();
            }
        };

        updateMatches();
        mm.desktop.addEventListener('change', updateMatches);
    }
}
customElements.define('custom-footer', CustomFooter);

/**
 * Footer
 */
class CustomLanguagePopup extends HTMLElement {
    constructor() {
        super();

        this.hasPrevScrollLock = false;
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
    }

    initElements() {
        this.popupLanguage = this;
        this.buttonClose = this.popupLanguage.querySelector('[data-mobile-language="close"]');
    }

    initEventListeners() {
        document.addEventListener('click', this.onEnterOpener.bind(this));
    }

    onEnterOpener(e) {
        const target = e.target;

        if (target.closest('[data-mobile-language="open"]')) {
            this.onDrawLanguage(target);
        }
    }

    onDrawLanguage(opener) {
        if (opener) opener.setAttribute('aria-expanded', true);
        this.popupLanguage.classList.add('is-open');

        if (!this.hasPrevScrollLock && document.documentElement.classList.contains('custom-scroll-lock')) {
            this.hasPrevScrollLock = true;
        } else {
            document.documentElement.classList.add('custom-scroll-lock');
        }

        this.popupLanguage.addEventListener('keydown', (event) => {
            cuiUtils.a11yKey({ type: 'trap' }, event); // 키보드 접근성
        });

        if (this.buttonClose) {
            this.buttonClose.focus();
            this.buttonClose.addEventListener('click', () => {
                this.onCloseLanguage(opener);
            }, { once: true });
        }
    }

    onCloseLanguage(opener) {
        if (this.popupLanguage && !this.popupLanguage.classList.contains('is-open')) return;

        this.popupLanguage.classList.remove('is-open');

        if (this.hasPrevScrollLock) {
            this.hasPrevScrollLock = false;
        } else {
            document.documentElement.classList.remove('custom-scroll-lock');
        }

        if (opener && opener.getAttribute('data-mobile-language') === 'open') {
            opener.setAttribute('aria-expanded', false);
            opener.focus();
        }
    }
}
customElements.define('custom-language-popup', CustomLanguagePopup);


/**
 * Dropdown List
 */
class CustomUIDropdown extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            button: 'custom-ui-dropdown__button',
            options: 'custom-ui-dropdown__options',
            state: {
                active: 'is-active',
                open: 'is-open',
                reverse: 'is-reverse'
            },
        };

        this.isHover = false;
    }

    get slideMode() {
        return this.getAttribute('slide');
    }

    get selectMode() {
        return this.hasAttribute('select');
    }

    get hoverMode() {
        return this.hasAttribute('hover');
    }

    get parentHeader() {
        return this.closest('.custom-header');
    }

    get topSearchButton() {
        return this.parentHeader.querySelector('[data-search-menu="open"]');
    }

    connectedCallback() {
        this.button = this.querySelector(`.${this.classes.button}`);
        this.options = this.querySelector(`.${this.classes.options}`);
        this.setValue();

        document.addEventListener('mousedown', this.onLeave.bind(this));
        this.addEventListener('click', this.onEnter.bind(this));

        if (this.hoverMode) {
            this.addEventListener('mouseenter', this.onEnter.bind(this), true);
            this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
        }
    }

    disconnectedCallback() {
        document.removeEventListener('mousedown', this.onLeave.bind(this));
    }

    setTranslateY() {
        let translateY;

        if (this.slideMode === 'up') {
            translateY = 'translateY(100%)';
        } else if (this.slideMode === 'down') {
            translateY = 'translateY(-100%)';
        }
        this.options.style.transition = 'transform .4s';
        this.options.style.transform = translateY;
    }

    open(a11y, callback) {
        const isClippedByViewport = () => {
            // 레이어 하단이 viewport에서 짤리는지 체크
            return this.options.getBoundingClientRect().bottom >= window.innerHeight;
        };

        const onOpenEnd = () => {
            this.classList.add(this.classes.state.open);
            this.options.offsetHeight;
            requestAnimationFrame(() => {
                if (this.slideMode) {
                    this.options.style.transform = 'translateY(0)';
                }

                if (a11y) {
                    const prev = this.options.querySelector(`li.${this.classes.state.active}`) ?? this.options.querySelector(`li:first-child`);
                    if (prev) prev.querySelector('a, input').focus(); // 선택된 항목으로 포커스

                    this.options.addEventListener('keydown', (event) => {
                        const items = Array.from(this.options.querySelectorAll('li'));
                        const focusedElement = event.target.closest('li');
                        const currentIndex = focusedElement ? items.indexOf(focusedElement) : -1;

                        const focusPrevItem = () => {
                            event.preventDefault();
                            if (currentIndex > 0) {
                                const prevItem = items[currentIndex - 1];
                                const prevFocusElement = prevItem.querySelector('a, input');
                                prevFocusElement?.focus();
                            }
                        }

                        const focusNextItem = () => {
                            event.preventDefault();
                            if (currentIndex < items.length - 1) {
                                const nextItem = items[currentIndex + 1];
                                const nextFocusElement = nextItem.querySelector('a, input');
                                nextFocusElement?.focus();
                            }
                        }

                        switch (event.key) {
                            case 'Escape':
                                event.preventDefault();
                                this.close(true);
                                break;

                            case 'ArrowDown':
                                focusNextItem();
                                break;

                            case 'ArrowUp':
                                focusPrevItem();
                                break;

                            case 'Tab':
                                const lastItem = items[items.length - 1];
                                const firstItem = items[0];

                                if (event.shiftKey) {
                                    focusPrevItem();
                                    if (focusedElement === firstItem) this.close(true);

                                } else if (!event.shiftKey) {
                                    focusNextItem();
                                    if (focusedElement === lastItem) this.close(true);
                                }
                                break;

                            case 'Enter':
                                if (focusedElement && focusedElement.querySelector('input')) {
                                    event.preventDefault();
                                    this.onEnter(event);
                                }
                                break;

                            default:
                                break;
                        }
                    });
                }
            });
        };

        if (this.slideMode) {
            this.setTranslateY();
            setTimeout(() => {
                onOpenEnd();
            }, 50);
        } else {
            onOpenEnd();
        }

        if (isClippedByViewport()) {
            this.classList.add(this.classes.state.reverse);
            this.options.style.bottom = cuiUtils.getComputed.height(this.button) + 'px';
        }

        if (this.parentHeader) { // active GNB
            this.parentHeader.classList.add('is-active');
        }

        if (callback && typeof callback === 'function') callback();
    }

    close(a11y, callback) {
        const onCloseEnd = () => {
            this.classList.remove(this.classes.state.open, this.classes.state.reverse);
            this.options.style.bottom = '';
            this.options.style.transform = '';
            this.options.style.transition = '';

            if (a11y) {
                this.button.focus();
            }
        };

        if (this.slideMode) {
            this.setTranslateY();

            const duration = cuiUtils.getTransitionDuration(this.options);
            setTimeout(() => {
                onCloseEnd();
            }, duration);
        } else {
            onCloseEnd();
        }

        if (this.parentHeader && !this.topSearchButton?.classList.contains('is-active')) { // inactive GNB
            this.parentHeader.classList.remove('is-active');
        }

        if (callback && typeof callback === 'function') callback();
    }

    onEnter(e) {
        const target = e.target;

        if (target.closest(`.${this.classes.button}`)) {
            const isHoverEvent = e.type === 'mouseenter';

            if (this.classList.contains(this.classes.state.open)) {
                if (!isHoverEvent) {
                    if (this.isHover) return;
                    this.close(true);
                }
            } else {
                if (isHoverEvent) {
                    this.open(false, () => {
                        this.isHover = true;
                    });
                } else {
                    this.open(true);
                }
            }
        }

        if (this.selectMode) {
            const optionItem = target.closest(`.${this.classes.options} li`);
            if (optionItem && !optionItem.classList.contains(this.classes.state.active)) {
                const prev = this.options.querySelector(`li.${this.classes.state.active}`);
                if (prev) {
                    prev.classList.remove(this.classes.state.active);
                    const prevInput = prev.querySelector('input');
                    if (prevInput) prevInput.checked = false;
                }

                optionItem.classList.add(this.classes.state.active);
                this.setValue(optionItem);
                this.close(true);

                // 커스텀 이벤트 추가
                this.dispatchChangeEvent(optionItem);
            }
        }
    }

    onLeave(e) {
        const target = e.target;
        if (e.button === 0) {
            // 바닥 페이지 왼클릭시 닫히게
            if (!this.contains(target) && this.classList.contains(this.classes.state.open)) {
                this.close(false);
            }
        }
    }

    onMouseLeave() {
        if (this.classList.contains(this.classes.state.open)) {
            this.close(false, () => {
                this.isHover = false;
            });
        }
    }

    setValue(item) { // select mode 이고 선택된 옵션값이 있을때(.is-active), 해당 옵션값으로 버튼명 변경
        const selected = item ? item : this.options.querySelector(`li.${this.classes.state.active}`);
        if (this.selectMode && selected) {
            this.button.textContent = selected.textContent;
            const input = selected.querySelector('input');
            if (input) input.checked = true;
        }
    }

    dispatchChangeEvent(item) {
        const customEvent = new CustomEvent('custom-ui-dropdown:change', {
            detail: {
                index: Array.from(this.options.querySelectorAll('li')).indexOf(item),
                element: item.querySelector('a, input'),
                parentElement: item
            },
            bubbles: false
        });

        this.dispatchEvent(customEvent);
    }
}
customElements.define('custom-ui-dropdown', CustomUIDropdown);


const commonUI = {
    bottomPageTop(rootEl = document) {
        const bottomEl = rootEl.querySelectorAll('.custom-bottom-gotop');

        bottomEl.forEach(section => {
            const button = section.querySelector('.custom-footer-gotop__button');

            button.addEventListener('click', (e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            })
        });
    },
}

const pageMain = {
    utils: {
        random(min, max) {
            const delta = max - min;
            return (direction = 1) => (min + delta * Math.random()) * direction;
        },
        swiperLazyPlaceholder(swiper) {
            if (swiper.params?.lazy?.enabled) {
                const slides = swiper.slides || [];
                const lazyClassName = swiper.params.lazy.elementClass;
                const preloaderClassName = swiper.params.lazy.preloaderClass;

                slides.forEach(slide => {
                    const lazyImg = slide.querySelector(`.${lazyClassName}`);
                    if (lazyImg && !lazyImg.getAttribute('src')) {
                        lazyImg.setAttribute('src', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAAtJREFUGFdjYAACAAAFAAGq1chRAAAAAElFTkSuQmCC');
                        if (!slide.querySelector(`.${preloaderClassName}`)) {
                            const target = lazyImg.closest('picture') || lazyImg;
                            target.insertAdjacentHTML('afterend', `<div class="${preloaderClassName}"></div>`);
                        }
                    }
                });
            }
        }
    },

    aos: {
        scrollCollect: 500,
        setClassIfVisible($el, offset = 0) {
            const scrollTop = $(window).scrollTop();
            if ($el.length && scrollTop >= $el.offset().top - this.scrollCollect - offset) {
                $el.addClass("scroll");
            }
        },
        sectionAos() {
            this.setClassIfVisible($(".campagin-sec"));
            this.setClassIfVisible($(".best-sec"));
            this.setClassIfVisible($(".new-sec"));
            this.setClassIfVisible($(".branding-sec"), 300);
        },
        init() {
            // console.log('aos');
            this.sectionAos();
            $(window).on("scroll", () => this.sectionAos());
        }
    },

    blurEffect: {
        init(rootEl = document) {
            const rand = pageMain.utils;
            const randomX = rand.random(-400, 400);
            const randomY = rand.random(-200, 200);
            const randomTime = rand.random(6, 12);
            const randomTime2 = rand.random(5, 6);
            const randomAngle = rand.random(-30, 150);

            const moveX = (target, dir) => {
                gsap.to(target, randomTime(), {
                    x: randomX(dir),
                    ease: Sine.easeInOut,
                    onComplete: () => moveX(target, dir * -1)
                });
            };
            const moveY = (target, dir) => {
                gsap.to(target, randomTime(), {
                    y: randomY(dir),
                    ease: Sine.easeInOut,
                    onComplete: () => moveY(target, dir * -1)
                });
            };
            const rotate = (target, dir) => {
                gsap.to(target, randomTime2(), {
                    rotation: randomAngle(dir),
                    ease: Sine.easeInOut,
                    onComplete: () => rotate(target, dir * -1)
                });
            };

            const blurs = gsap.utils.toArray(".main-page-bg .blur") || [];

            rootEl.querySelectorAll('.main-page-bg').forEach(section => {
                blurs.forEach(blur => {
                    gsap.set(blur, {
                        x: randomX(-1),
                        y: randomY(1),
                        rotation: randomAngle(-1),
                    });
                    moveX(blur, 2.5);
                    moveY(blur, 4);
                    rotate(blur, 1);
                });
            });
        }
    },

    keyVisual: {
        setCSSProperty() {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);

            const kvEl = document.querySelector('.key-visual-swiper');
            if (kvEl) {
                const kvHeight = kvEl.offsetHeight;
                const kvImg = kvEl.querySelector('.key-img-box');
                if (kvImg) {
                    const kvImgHeight = kvImg.offsetHeight;
                    const scaleValue = kvImgHeight !== 0 ? kvHeight / kvImgHeight : 1;
                    document.documentElement.style.setProperty('--main-kv-scale', Math.round(scaleValue * 1000) / 1000);
                }
            }
        },
        setKeyTitle() {
            for (let k = 0; k < $(".key-visual-slide").length; k++) {
                const keyTit = $(".key-visual-slide").eq(k).find(".key-slide-content .key-title");
                if (!keyTit.length) continue;

                const isThreeLine = keyTit.hasClass("three");
                const keyTitH = keyTit.height() * (isThreeLine ? 0.54 : 0.73);

                if (window.innerWidth > 1440) {
                    const calcH = h => h * 0.305 - 60;
                    const compareH = Math.max(calcH($(window).height()), calcH($(".key-visual-slide").height()));
                    keyTit.css('transform', `translateY(${compareH - keyTitH}px)`);
                } else if (window.innerWidth <= 1440 && window.innerWidth > 720) {
                    keyTit.css('transform', `translateY(${isThreeLine ? 76 : 75}px)`);
                } else {
                    const calcH = h => h * 0.2697 - 60;
                    const compareH = Math.max(calcH(window.innerHeight), calcH($(".key-visual-slide").height()));
                    keyTit.css('transform', `translateY(${compareH - keyTitH}px)`);
                }
            }
        },
        initKeySwiper() {
            let oldIndex = 0;
            const changeColorIndicator = swiper => {
                const isBlack = swiper.slides[swiper.activeIndex].querySelector(".key-text-wrap")?.classList.contains("black");
                swiper.el.classList.toggle("is-active-color--black", isBlack);
            };

            const slideLength = document.querySelectorAll(".key-visual-swiper .key-visual-slide").length > 1;

            !slideLength && (document.querySelector('.key-visual-swiper .playPause').style.display = 'none');

            const keySwiper = new Swiper(".key-visual-swiper", {
                loop: slideLength,
                effect: "fade",
                fadeEffect: { crossFade: true },
                speed: 1000,
                loopPreventsSliding: true,
                shortSwipes: 10,
                keyboard: { enabled: true },
                autoplay: {
                    delay: 5000,
                    waitForTransition: true,
                    disableOnInteraction: false
                },
                navigation: {
                    nextEl: ".key-visual-swiper .swiper-button-next",
                    prevEl: ".key-visual-swiper .swiper-button-prev"
                },
                pagination: {
                    el: ".key-visual-swiper .swiper-pagination",
                    clickable: true,
                    renderBullet(index, className) {
                        return `<span class="${className}" ap-click-area="Main" ap-click-name="Click - Main Indicator" ap-click-data="${index + 1}" tabindex="0">
                            <svg class="fp-arc-loader" width="16" height="16" viewBox="0 0 16 16">
                                <circle class="path on" cx="8" cy="8" r="8" fill="none" transform="rotate(-90 8 8)" stroke="#FFF" stroke-opacity="1" stroke-width="1px"></circle>
                                <circle class="path2" cx="8" cy="8" r="1.78" fill="#FFF"></circle>
                            </svg>
                        </span>`;
                    }
                },
                preloadImages: false,
                lazy: {
                    enabled: true,
                    loadOnTransitionStart: true,
                    loadPrevNext: true
                },
                on: {
                    init() {
                        const _this = this;
                        pageMain.utils.swiperLazyPlaceholder(_this);
                        setTimeout(() => _this.autoplay.start(), 100);
                        changeColorIndicator(this);

                        $(".key-swiper-control-wrap .playPause").on("click", function (e) {
                            e.preventDefault();
                            const $btn = $(this);
                            const isPause = $btn.hasClass("pause");
                            $btn.toggleClass("pause play").attr("ap-click-data", isPause ? "Play" : "Pause");
                            $btn.children(".ap2022-sr-only").text(isPause ? "Play" : "Pause");

                            if (isPause) {
                                _this.autoplay.stop();
                                $(".key-swiper-control-wrap .swiper-pagination-bullet .path").addClass("pause");
                            } else {
                                _this.autoplay.start();
                                $(".key-swiper-control-wrap .swiper-pagination-bullet .path").removeClass("pause");
                                const target = $(".key-swiper-control-wrap .common-bullet .path");
                                target.removeClass("on").outerWidth();
                                target.addClass("on");
                            }
                        });
                    },
                    slideChange() {
                        changeColorIndicator(this);
                        $(".key-visual-slide").css("visibility", "hidden");
                        $(".key-visual-slide").eq(this.activeIndex).css("visibility", "visible");
                        $(".key-visual-slide").eq(oldIndex).css("visibility", "visible");

                        $(".key-img-box").addClass("change");
                        setTimeout(() => {
                            $(".key-img-box").removeClass("change");
                        }, 1000);
                    },
                    slideChangeTransitionEnd() {
                        oldIndex = this.activeIndex;
                    }
                }
            });

            keySwiper.autoplay.stop();
        },
        mainStart() {
            if ($(".key-visual-swiper").hasClass("init")) {
                this.initKeySwiper();
            }
            $(".key-visual-swiper").removeClass("init").addClass("start");
        },
        init(rootEl = document) {
            const keyVisualEl = rootEl.querySelectorAll('.key-visual-sec');

            keyVisualEl.forEach(section => {
                this.setCSSProperty();
                this.setKeyTitle();
                this.mainStart();
                // this.initKeySwiper();
                window.addEventListener("resize", () => {
                    this.setCSSProperty();
                    this.setKeyTitle();
                });
            });
        }
    },

    best: {
        init(rootEl = document) {
            const bestEl = rootEl.querySelectorAll('.best-sec');

            bestEl.forEach(section => {

                // console.log('best');
                // if (!$(".best-sec").hasClass("scroll")) return;

                // if (!$('.best-visual-slider').hasClass('swiper-initialized')) {
                //     this.initBestSwiper();
                //     this.bindCursorEvents();
                //     this.applySlideColors();
                // }

                $(window).on("scroll resize", () => {
                    if ($('.best-sec').hasClass('scroll') && !$('.best-visual-slider').hasClass('swiper-initialized')) {
                        this.initBestSwiper();
                        this.bindCursorEvents();
                        this.applySlideColors();
                    }
                });

                $(".swiper-btn-play-wrap").on("click", function () {
                    const $this = $(this);
                    $this.toggleClass("stop");
                    const swiper = document.querySelector(".best-visual-slider").swiper;
                    if ($this.hasClass("stop")) swiper.autoplay.stop();
                    else swiper.autoplay.start();
                });

            })

        },
        initBestSwiper() {
            const visualCount = $(".best-visual-slider .swiper-slide").length;
            const visualSlideOpt = visualCount > 1 ? {
                slidesPerView: 1,
                loop: true,
                effect: "fade",
                fadeEffect: { crossFade: true },
                grabCursor: true,
                speed: 800,
                shortSwipes: 20,
                simulateTouch: false,
                preloadImages: false,
                lazy: { enabled: true, loadOnTransitionStart: true, loadPrevNext: true },
                on: { init(swiper) { pageMain.utils.swiperLazyPlaceholder(swiper); } }
            } : { slidesPerView: 1, loop: false, grabCursor: false };

            const infoSlideOpt = visualCount > 1 ? {
                slidesPerView: 1,
                loop: true,
                effect: "fade",
                fadeEffect: { crossFade: true },
                parallax: true,
                grabCursor: true,
                shortSwipes: 10,
                speed: 800,
                keyboard: { enabled: true },
                autoplay: { delay: 4000, disableOnInteraction: false, waitForTransition: true },
                pagination: {
                    el: ".best-sec .swiper-pagination",
                    clickable: true,
                    renderBullet: (index, className) => `<span class="${className}" ap-click-area="Main" ap-click-name="Click - Best Seller Indicator" ap-click-data="${index + 1}" tabindex="0"><svg class="fp-arc-loader" width="16" height="16" viewBox="0 0 16 16"><circle class="path on" cx="8" cy="8" r="8" fill="none" transform="rotate(-90 8 8)" stroke="#444" stroke-opacity="1" stroke-width="1px"></circle><circle class="path2" cx="8" cy="8" r="1.78" fill="#444"></circle></svg></span>`
                },
                navigation: {
                    nextEl: ".best-sec .swiper-btn-next",
                    prevEl: ".best-sec .swiper-btn-prev"
                },
                preloadImages: false,
                lazy: { enabled: true, loadOnTransitionStart: true, loadPrevNext: true },
                on: {
                    init(swiper) {
                        pageMain.utils.swiperLazyPlaceholder(swiper);
                        setTimeout(() => swiper.autoplay.start(), 100);
                        $(".best-sec .playPause").on("click", function (e) {
                            e.preventDefault();
                            const $btn = $(this);
                            const isPause = $btn.hasClass("pause");
                            const bullet = $(".best-sec .common-bullet .swiper-pagination-bullet .path");
                            $btn.toggleClass("pause play").attr("ap-click-data", isPause ? "Play" : "Pause");
                            $btn.children(".ap2022-sr-only").text(isPause ? "Play" : "Pause");
                            if (isPause) {
                                swiper.autoplay.stop();
                                bullet.addClass("pause");
                            } else {
                                swiper.autoplay.start();
                                bullet.removeClass("pause").removeClass("on").outerWidth();
                                bullet.addClass("on");
                            }
                        });
                    },
                    slideChange(swiper) {
                        if (swiper.autoplay.running) swiper.autoplay.start();
                    }
                }
            } : { slidesPerView: 1, loop: false, grabCursor: false };

            const visualSwiper = new Swiper(".best-visual-slider", visualSlideOpt);
            const infoSwiper = new Swiper(".best-info-slider", infoSlideOpt);

            infoSwiper.controller.control = visualSwiper;
            visualSwiper.controller.control = infoSwiper;

            infoSwiper.on("slideChange", () => visualSwiper.lazy.load());
            visualSwiper.on("slideChange", () => {
                if (infoSwiper.autoplay.running) infoSwiper.autoplay.pause();
                infoSwiper.lazy.load();
            });
        },
        bindCursorEvents() {
            if ($(".best-visual-slider .swiper-slide").length <= 1) {
                $(".best-sec .swiper-main-btn-wrap > a").css("cursor", "default");
                $(".best-sec .swiper-main-btn-wrap").css("cursor", "default");
                return;
            }
            const $cursor = $("#best-cursor");
            $(".best-info-slider .swiper-btn-prev").on("mouseenter", () => {
                $cursor.addClass("on");
                $cursor.find(".left-cursor").css("opacity", "0.8");
            }).on("mouseleave", () => {
                $cursor.removeClass("on");
                $cursor.find(".left-cursor").css("opacity", "0");
            });
            $(".best-info-slider .swiper-btn-next").on("mouseenter", () => {
                $cursor.addClass("on");
                $cursor.find(".right-cursor").css("opacity", "0.8");
            }).on("mouseleave", () => {
                $cursor.removeClass("on");
                $cursor.find(".right-cursor").css("opacity", "0");
            });
            $(".best-info-slider").on("mousemove", e => {
                $cursor.css({ left: 0, top: 0, transform: `translate(${e.pageX}px,${e.clientY}px)` });
            });
            $(window).on("mousewheel", e => {
                const $btn = $(".best-info-slider .swiper-btn-prev");
                if (!$btn.length) return;
                const scrollTop = $(window).scrollTop();
                const windowH = $(window).height();
                const y = e.pageY;
                const offset = $btn.offset().top;
                const height = $btn.height();
                if (scrollTop + windowH > offset && scrollTop < offset + height) {
                    if (y < offset || y > offset + height) {
                        setTimeout(() => $cursor.find(".arrow-cursor").css("opacity", 0), 300);
                        $cursor.removeClass("on");
                    }
                }
            });
        },
        applySlideColors() {
            $(".best-info-slider .swiper-slide").each(function () {
                const $slide = $(this);
                const color = $slide.attr("data-background-color");
                if (color) {
                    $slide.find(".best-info-cont").css("backgroundColor", `${color}14`);
                    $slide.find(".tag").css("color", color);
                }
            });
        }
    },

    new: {
        newSwiper: null,
        init(rootEl = document) {
            const newEl = rootEl.querySelectorAll('.new-sec');

            newEl.forEach(section => {

                if ($(".new-sec").hasClass("scroll")) {
                    this.initSwiper();
                }

                $(window).on("scroll", () => {
                    if ($(".new-sec").hasClass("scroll") && !$(".new-sec").hasClass("init")) {
                        this.initSwiper();
                    }
                });

                // let resizeTimer = null;
                // $(window).on("resize", () => {
                //     if ($(".new-sec").hasClass("scroll") && !$(".new-sec").hasClass("init")) {
                //         this.initSwiper();
                //     }

                //     clearTimeout(resizeTimer);
                //     resizeTimer = setTimeout(() => this.rebuildSwiper(), 300);
                // });

                this.bindControls();
                this.bindCursor();

            });

        },
        initSwiper() {
            if ($(".new-sec").hasClass("on")) return;

            let prevSlideWd = 0;
            const $slides = $(".new-sec .new-slider .swiper-slide");
            $slides.each(function (i) {
                if (i > 0) {
                    prevSlideWd += $slides.eq(i - 1).width();
                    $(this).css("transform", `translateX(-${prevSlideWd}px)`);
                }
            });

            $(".new-sec").addClass("init");
            setTimeout(() => {
                $slides.css("transform", "translateX(0px)");
            }, 1000);

            setTimeout(() => {
                $(".new-sec").addClass("on");
            }, 2000);

            setTimeout(() => {
                this.newSwiper = new Swiper(".new-slider", {
                    slidesPerView: "auto",
                    loop: false,
                    touchStartPreventDefault: false,
                    shortSwipes: 20,
                    a11y: false,
                    keyboard: { enabled: true },
                    autoplay: { delay: 3000, disableOnInteraction: false },
                    pagination: {
                        el: ".new-sec .swiper-pagination",
                        clickable: true,
                        renderBullet: (index, className) => `<span class="${className}" ap-click-area="Main" ap-click-name="Click - New Arrivals Indicator" ap-click-data="${index + 1}" tabindex="0"><svg class="fp-arc-loader" width="16" height="16" viewBox="0 0 16 16"><circle class="path on" cx="8" cy="8" r="8" fill="none" transform="rotate(-90 8 8)" stroke="#444" stroke-opacity="1" stroke-width="1px"></circle><circle class="path2" cx="8" cy="8" r="1.78" fill="#444"></circle></svg></span>`
                    },
                    on: {
                        touchStart() {
                            $(this.el).addClass("grab-on");
                            $("#new-cursor").addClass("grab-on");
                        },
                        touchEnd() {
                            $(this.el).removeClass("grab-on");
                            $("#new-cursor").removeClass("grab-on");
                        }
                    }
                });
            }, 3000);
        },
        rebuildSwiper() {
            $(".new-sec .swiper-pagination-bullet").remove();
            this.newSwiper.destroy();
            this.newSwiper = undefined;
            this.initSwiper();
            if ($(".new-sec .playPause").hasClass("play")) {
                $(".new-sec .playPause").removeClass("play").addClass("pause").attr("ap-click-data", "Pause");
                $(".new-sec .playPause").children(".ap2022-sr-only").text("Pause");
            }
        },
        bindControls() {
            $(".new-sec .playPause").on("click", (e) => {
                e.preventDefault();
                const $btn = $(e.currentTarget);
                const isPause = $btn.hasClass("pause");
                $btn.toggleClass("pause play").attr("ap-click-data", isPause ? "Play" : "Pause");
                $btn.children(".ap2022-sr-only").text(isPause ? "Play" : "Pause");
                if (this.newSwiper) {
                    if (isPause) {
                        this.newSwiper.autoplay.stop();
                        $(".new-sec .common-bullet .swiper-pagination-bullet .path").addClass("pause");
                    } else {
                        this.newSwiper.autoplay.start();
                        const target = $(".new-sec .common-bullet .path");
                        target.removeClass("pause on");
                        void target.outerWidth();
                        target.addClass("on");
                    }
                }
            });
        },
        bindCursor() {
            const $cursor = $("#new-cursor");
            const $slider = $(".new-sec .new-slider");
            $slider.on("mouseenter", () => $cursor.addClass("on").removeClass("off"));
            $slider.on("mouseleave", () => $cursor.removeClass("on").addClass("off"));
            $slider.on("mousemove", e => {
                $cursor.css({ left: 0, top: 0, transform: `translate(${e.pageX}px,${e.clientY}px)` });
            });
            $(window).on("mousewheel", e => {
                const scrollTop = $(window).scrollTop();
                const windowH = $(window).height();
                const offset = $slider.offset().top;
                const height = $slider.height();
                if (scrollTop + windowH > offset && scrollTop < offset + height) {
                    const y = e.pageY;
                    if (y < offset || y > offset + height) {
                        if ($cursor.hasClass("on")) {
                            $cursor.removeClass("on").addClass("off");
                        }
                    }
                }
            });
        },
        newSwiper: null
    },

    branding: {
        init(rootEl = document) {
            const brandingEl = rootEl.querySelectorAll('.branding-sec');

            brandingEl.forEach(section => {
                $(window).on("scroll resize", () => {
                    if ($(".branding-sec").hasClass("scroll") && !$((".branding-swiper-container")).hasClass("swiper-initialized")) {
                        this.initBrandingSwiper();
                    }
                });

                if ($(".branding-sec").hasClass("scroll")) {
                    this.initBrandingSwiper();
                }
            });
        },

        initBrandingSwiper() {
            new Swiper(".branding-swiper-container", {
                speed: 800,
                loop: true,
                loopPreventsSlide: true,
                keyboard: { enabled: true },
                autoplay: {
                    delay: 5000,
                    disableOnInteraction: false,
                    stopOnLastSlide: false,
                },
                effect: "fade",
                fadeEffect: { crossFade: true },
                slidesPerView: 1,
                navigation: {
                    nextEl: ".branding-swiper-container .swiper-button-next",
                    prevEl: ".branding-swiper-container .swiper-button-prev",
                },
                pagination: {
                    el: ".branding-swiper-container .swiper-pagination",
                    type: "bullets",
                    bulletActiveClass: "active",
                    bulletClass: "bullet",
                    clickable: true,
                    renderBullet(index, className) {
                        const slideReal = $(".branding-slide").not(".swiper-slide-duplicate");
                        const slideTxt = slideReal.map((i, el) => $(el).data("brandingtitle")).get();
                        let label = index + 1;
                        let number = label < 10 ? "0" + label : label;
                        return `<li class="${className}"><a href="javascript:;" ap-click-area="Main" ap-click-name="Click - Branded Contents Indicator" ap-click-data="${number}.${slideTxt[index]}" tabindex="-1"><span class="slide-num">${number}</span><span class="dot">. </span><span class="slide-txt">${slideTxt[index]}</span></a></li>`;
                    }
                },
                breakpoints: {
                    750: {
                        noSwiping: false,
                    },
                },
                preloadImages: false,
                lazy: {
                    enabled: true,
                    loadOnTransitionStart: true,
                    loadPrevNext: true
                },
                on: {
                    init() {
                        const _this = this;
                        pageMain.utils.swiperLazyPlaceholder(_this);
                        let mvInter = setInterval(() => {
                            $(".branding-swiper-container .swiper-pagination li").removeClass("active");
                            if ($(".branding-swiper-container").hasClass("active")) {
                                _this.autoplay.start();
                                $(".branding-swiper-container .swiper-pagination li").eq(0).addClass("active");
                                clearInterval(mvInter);
                            }
                        }, 100);

                        $(".branding-swiper-container .pagination .playPause").on("click", function (e) {
                            e.preventDefault();
                            const $btn = $(this);
                            const isPause = $btn.hasClass("pause");
                            $btn.toggleClass("pause play").attr("ap-click-data", isPause ? "Play" : "Pause");
                            $btn.children(".ap2022-sr-only").text(isPause ? "Play" : "Pause");
                            if (isPause) {
                                _this.autoplay.stop();
                            } else {
                                _this.autoplay.start();
                            }
                        });
                    }
                }
            });
        }
    }
};

/**
 * Modal
 */
const APModal = {
    defaults: {
        layerRootSelectorName: '.ap2022-popup',
        popupInnerSelectorName: '.ap2022-popup__inner',
        popupScrollSelectorName: '.ap2022-popup__scrollable',
        readyClass: 'is-ready',
        activeClass: 'is-active',
        closeOutClick: false,
        on: {
            layerOpenStart: null,
            layerOpenEnd: null,
            layerCloseStart: null,
            layerCloseEnd: null,
        },
    },

    activeLayerList: [],
    lastFocusElement: null,
    tabPressTimer: null,
    closeTimer: null,
    params: {},

    init(userParams = {}) {
        this.params = this.mergeParams(this.defaults, userParams);
        this.bindEvents();
    },

    mergeParams(defaults, user) {
        return Object.keys(defaults).reduce((merged, key) => {
            merged[key] =
                typeof defaults[key] === 'object' && !Array.isArray(defaults[key])
                    ? { ...defaults[key], ...(user[key] || {}) }
                    : user[key] !== undefined
                    ? user[key]
                    : defaults[key];
            return merged;
        }, {});
    },

    bindEvents() {
        document.addEventListener('keydown', this.onKeyDown.bind(this));
        document.addEventListener('click', this.onClick.bind(this));
    },

    focusableSelectors: '[tabindex], a, button, input, select, textarea, details',

    onKeyDown(e) {
        const active = this.getActiveLayer();
        if (!active) return;

        const focusables = active.querySelectorAll(this.focusableSelectors);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.key === 'Escape') {
            e.preventDefault();
            this.close(active);
            return;
        }

        if (e.key === 'Tab') {
            if (e.shiftKey && e.target === first) {
                e.preventDefault();
                this.focusLater(last);
            } else if (!e.shiftKey && e.target === last) {
                e.preventDefault();
                this.focusLater(first);
            }
        }
    },

    onClick(e) {
        const activeObj = this.activeLayerList.at(-1);
        if (!activeObj) return;

        const { element, params } = activeObj;

        const isOutsideClick =
            e.target.closest(this.params.layerRootSelectorName) &&
            !e.target.closest(this.params.popupInnerSelectorName);

        if (params.closeOutClick && isOutsideClick) {
            this.close(element);
        }
    },

    focusLater(el) {
        clearTimeout(this.tabPressTimer);
        this.tabPressTimer = setTimeout(() => el?.focus(), 50);
    },

    getActiveLayer() {
        return this.activeLayerList.at(-1)?.element || null;
    },

    isElement(el) {
        return el instanceof HTMLElement;
    },

    hasScroll(el) {
        if (el.scrollHeight > el.clientHeight) {
            el.setAttribute('tabindex', 0);
        }
    },

    open(selector, customParams = {}) {
        const layer = this.isElement(selector)
            ? selector
            : document.querySelector(selector);
        if (!layer) return;

        const params = this.mergeParams(this.params, customParams);
        this.lastFocusElement = document.activeElement || document.body;
        this.activeLayerList.push({ element: layer, params });
        
        params.on.layerOpenStart?.();

        layer.style.display = '';
        requestAnimationFrame(() => {
            layer.classList.add(params.readyClass);
            requestAnimationFrame(() => {
                layer.classList.add(params.activeClass);

                const inner = layer.querySelector(params.popupInnerSelectorName);
                inner?.setAttribute('tabindex', 0);

                const focusables = layer.querySelectorAll(this.focusableSelectors);
                focusables[0]?.focus();

                params.on.layerOpenEnd?.();

                const scrollable = layer.querySelector(params.popupScrollSelectorName);
                if (scrollable) {
                    this.hasScroll(scrollable);
                    scrollable.querySelectorAll('img').forEach((img) => {
                        if (!img.complete) {
                            img.addEventListener('load', () => this.hasScroll(scrollable));
                        }
                    });
                }
            });
        });
    },

    close(selector) {
        const layer = this.isElement(selector)
            ? selector
            : document.querySelector(selector);
        if (!layer) return;

        const index = this.activeLayerList.findIndex((obj) => obj.element === layer);
        if (index === -1) return;

        const params = this.activeLayerList[index].params;
        params.on.layerCloseStart?.();
        layer.classList.remove(params.activeClass);

        const duration =
            parseFloat(getComputedStyle(layer).transitionDuration) * 1000 || 0;

        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => {
            layer.classList.remove(params.readyClass);
            layer.style.display = 'none';
            this.activeLayerList.splice(index, 1);

            const last = this.getActiveLayer();
            last
                ? last.querySelector(this.focusableSelectors)?.focus()
                : this.lastFocusElement?.focus();

            if (!last) this.lastFocusElement = null;

            params.on.layerCloseEnd?.();
        }, duration);
    },

    closeActiveLayer(selector) {
        const last = this.getActiveLayer();
        if (!last) return;

        if (!selector || selector === last) {
            this.close(last);
        }
    },

    instantiate() {
        if (document.querySelector('[data-ap2022-modal]')) {
            this.init();
        }
    },
};
